/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        InterventionRecap.tsx
 * @description Modal de recapitulatif d'intervention terminee
 *              Affiche la chronologie complete avec horodatages
 *
 * @features    - Chronologie complete des actions
 *              - Statistiques (duree, zones, progression)
 *              - Telechargement du PDF
 *              - Partage par email
 *
 * @version     2.2
 * @author      Equipe FALCON
 * =============================================================================
 */

import { clsx } from 'clsx';
import { getBackendUrl } from '@/services/api';
import type { Deneignement, DeneignementZone } from '@/types';
import { PRIORITE_LABELS, STATUT_LABELS } from '@/types';
import {
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
  ShareIcon,
  UserGroupIcon,
  TruckIcon,
  PhotoIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid, XCircleIcon as XCircleSolid } from '@heroicons/react/24/solid';
import { useState } from 'react';

interface InterventionRecapProps {
  intervention: Deneignement;
  zones: DeneignementZone[];
  onClose: () => void;
  onDownloadPdf: () => void;
}

/**
 * Modal de recapitulatif d'intervention
 * Affiche toutes les informations de l'intervention terminee
 */
// Labels pour les motifs de non-realisation
const MOTIF_NON_REALISATION_LABELS: Record<string, string> = {
  conditions_meteo: 'Conditions météo défavorables',
  panne_materiel: 'Panne de matériel',
  urgence_autre_intervention: 'Urgence - autre intervention prioritaire',
  parking_occupe: 'Parking occupé / inaccessible',
  annulation_compagnie: 'Annulation par la compagnie',
  manque_effectif: "Manque d'effectif",
  autre: 'Autre motif',
};

export function InterventionRecap({
  intervention,
  zones,
  onClose,
  onDownloadPdf,
}: InterventionRecapProps) {
  const [showPhotoModal, setShowPhotoModal] = useState<'avant' | 'apres' | null>(null);

  // Determiner si les travaux ont ete effectues
  const travauxEffectues = intervention.travauxEffectues !== false;

  // Calculer la duree totale
  const getDureeFormatted = () => {
    if (!intervention.heureDebutReelle || !intervention.heureFinReelle) {
      return intervention.dureeIntervention
        ? `${Math.floor(intervention.dureeIntervention / 60)}h ${intervention.dureeIntervention % 60}min`
        : 'N/A';
    }
    const start = new Date(intervention.heureDebutReelle);
    const end = new Date(intervention.heureFinReelle);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return hours > 0 ? `${hours}h ${mins}min` : `${mins} min`;
  };

  // Calculer le nombre de zones terminees
  const zonesTerminees = zones.filter((z) => z.statut === 'termine').length;
  const totalZones = zones.length;

  // Formater l'heure
  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '--:--';
    return new Date(dateStr).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={clsx(
          'flex items-center justify-between p-4 border-b border-gray-200',
          travauxEffectues ? 'bg-green-50' : 'bg-orange-50'
        )}>
          <div className="flex items-center gap-2">
            {travauxEffectues ? (
              <CheckCircleSolid className="w-6 h-6 text-green-600" />
            ) : (
              <XCircleSolid className="w-6 h-6 text-orange-600" />
            )}
            <h2 className="text-lg font-bold text-gray-900">
              {travauxEffectues ? 'Intervention terminée' : 'Intervention clôturée'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={clsx(
              'p-2 rounded-full',
              travauxEffectues ? 'hover:bg-green-100 active:bg-green-200' : 'hover:bg-orange-100 active:bg-orange-200'
            )}
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Numero et parking */}
          <div className="text-center py-2">
            <p className="text-2xl font-bold text-gray-900">{intervention.numeroOrdre}</p>
            <div className="flex items-center justify-center gap-2 text-gray-600 mt-1">
              <MapPinIcon className="w-5 h-5" />
              <span className="font-medium">{intervention.parking?.code || 'N/A'}</span>
            </div>
          </div>

          {/* Resultat de l'intervention */}
          <div className={clsx(
            'rounded-xl p-4',
            travauxEffectues ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'
          )}>
            <div className="flex items-start gap-3">
              {travauxEffectues ? (
                <CheckCircleSolid className="w-8 h-8 text-green-500 flex-shrink-0" />
              ) : (
                <ExclamationTriangleIcon className="w-8 h-8 text-orange-500 flex-shrink-0" />
              )}
              <div className="flex-1">
                <h3 className={clsx(
                  'font-bold text-lg',
                  travauxEffectues ? 'text-green-800' : 'text-orange-800'
                )}>
                  {travauxEffectues ? 'Travaux réalisés' : 'Travaux non effectués'}
                </h3>
                {!travauxEffectues && intervention.motifNonRealisation && (
                  <p className="text-sm text-orange-700 mt-1">
                    <strong>Motif:</strong> {MOTIF_NON_REALISATION_LABELS[intervention.motifNonRealisation] || intervention.motifNonRealisation}
                  </p>
                )}
                {!travauxEffectues && intervention.commentaireNonRealisation && (
                  <p className="text-sm text-orange-600 mt-1">
                    <strong>Détail:</strong> {intervention.commentaireNonRealisation}
                  </p>
                )}
                {travauxEffectues && (
                  <p className="text-sm text-green-700 mt-1">
                    {zonesTerminees} zone(s) traitée(s) sur {totalZones}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Photos avant/apres */}
          {(intervention.photoAvant || intervention.photoApres) && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <PhotoIcon className="w-5 h-5" />
                Photos de l'intervention
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {/* Photo AVANT */}
                <div>
                  <p className="text-xs text-gray-500 mb-1 text-center">AVANT</p>
                  {intervention.photoAvant ? (
                    <button
                      onClick={() => setShowPhotoModal('avant')}
                      className="w-full relative group"
                    >
                      <img
                        src={getBackendUrl(intervention.photoAvant)}
                        alt="Photo avant"
                        className="w-full h-24 object-cover rounded-lg border border-gray-200 group-hover:opacity-90 transition-opacity"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="bg-black/50 text-white text-xs px-2 py-1 rounded">Agrandir</span>
                      </div>
                    </button>
                  ) : (
                    <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-400">Non fournie</span>
                    </div>
                  )}
                </div>
                {/* Photo APRES */}
                <div>
                  <p className="text-xs text-gray-500 mb-1 text-center">APRÈS</p>
                  {intervention.photoApres ? (
                    <button
                      onClick={() => setShowPhotoModal('apres')}
                      className="w-full relative group"
                    >
                      <img
                        src={getBackendUrl(intervention.photoApres)}
                        alt="Photo après"
                        className="w-full h-24 object-cover rounded-lg border border-gray-200 group-hover:opacity-90 transition-opacity"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="bg-black/50 text-white text-xs px-2 py-1 rounded">Agrandir</span>
                      </div>
                    </button>
                  ) : (
                    <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-gray-400">Non fournie</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Statistiques principales */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <ClockIcon className="w-6 h-6 text-blue-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-blue-700">{getDureeFormatted()}</p>
              <p className="text-xs text-blue-600">Duree totale</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-green-700">{zonesTerminees}/{totalZones}</p>
              <p className="text-xs text-green-600">Zones</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3 text-center">
              <UserGroupIcon className="w-6 h-6 text-purple-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-purple-700">{intervention.equipes?.length || 1}</p>
              <p className="text-xs text-purple-600">Equipe(s)</p>
            </div>
          </div>

          {/* Informations generales */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Informations
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Date</span>
                <span className="font-medium">{formatDate(intervention.dateIntervention)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Compagnie</span>
                <span className="font-medium">{intervention.compagnie.codeIata} - {intervention.compagnie.nom}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Priorite</span>
                <span className={clsx(
                  'px-2 py-0.5 rounded-full text-xs font-medium text-white',
                  intervention.priorite === 'critique' && 'bg-red-500',
                  intervention.priorite === 'urgente' && 'bg-orange-500',
                  intervention.priorite === 'normale' && 'bg-gray-500'
                )}>
                  {PRIORITE_LABELS[intervention.priorite]}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Statut</span>
                <span className="font-medium text-green-600">{STATUT_LABELS[intervention.statut]}</span>
              </div>
            </div>
          </div>

          {/* Chronologie */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Chronologie
            </h3>
            <div className="space-y-3">
              {/* Debut */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-bold text-sm">1</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Debut intervention</p>
                  <p className="text-sm text-gray-600">
                    {formatTime(intervention.heureDebutReelle)}
                  </p>
                </div>
              </div>

              {/* Zones */}
              {zones.filter(z => z.statut === 'termine').map((zone, index) => (
                <div key={zone.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircleSolid className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Zone {zone.zone.numero}: {zone.zone.nom}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>{formatTime(zone.heureFin)}</span>
                      {zone.dureeMinutes && (
                        <span className="text-gray-400">({zone.dureeMinutes} min)</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Fin */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <CheckCircleSolid className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Fin intervention</p>
                  <p className="text-sm text-gray-600">
                    {formatTime(intervention.heureFinReelle)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Commentaire si present */}
          {intervention.commentaire && (
            <div className="bg-yellow-50 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-yellow-700 mb-2">Commentaire</h3>
              <p className="text-sm text-yellow-800">{intervention.commentaire}</p>
            </div>
          )}
        </div>

        {/* Footer avec actions */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onDownloadPdf}
              disabled={intervention.statut !== 'validee'}
              className={clsx(
                'flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold',
                intervention.statut === 'validee'
                  ? 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              )}
              title={intervention.statut !== 'validee' ? 'PDF disponible après validation' : ''}
            >
              <DocumentArrowDownIcon className="w-5 h-5" />
              <span>Telecharger PDF</span>
            </button>
            <button
              onClick={onClose}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 active:bg-gray-100"
            >
              <span>Fermer</span>
            </button>
          </div>
          {intervention.statut !== 'validee' && (
            <p className="text-xs text-gray-500 text-center mt-2">
              Le PDF sera disponible après validation par le régulateur
            </p>
          )}
        </div>
      </div>

      {/* Modal photo agrandie */}
      {showPhotoModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80"
            onClick={() => setShowPhotoModal(null)}
          />
          <div className="relative max-w-2xl w-full">
            <button
              onClick={() => setShowPhotoModal(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:bg-white/20 rounded-full"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="p-2 bg-gray-100 text-center">
                <span className="text-sm font-semibold text-gray-700">
                  Photo {showPhotoModal === 'avant' ? 'AVANT' : 'APRÈS'}
                </span>
              </div>
              <img
                src={getBackendUrl(showPhotoModal === 'avant' ? intervention.photoAvant : intervention.photoApres)}
                alt={`Photo ${showPhotoModal}`}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InterventionRecap;
